import Spinner from "@/components/Spinner";
import { Button } from "@/components/ui/button";
import { Upload } from "lucide-react";
import { useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../../store/hooks";
import { pushModal } from "../../../../store/slices/modalsSlice";
import GeneralMessageModal from "../../../Modals/GeneralMessageModal";
import QuestionImg from "../../../quiz/QuestionImg";
import FileUploadProgressBar from "../../FileUploadProgressBar";
import {
  getAnswersCount,
  handlePublishQuestion,
  handleUploadFb,
  maxMinCount
} from "../utils";

const initialQuestionData = {
  title: null,
  imgSrc: null,
  qText: null,
  qAnsCount: null,
  qAns: null,
  qWeight: null,
  qCat: null,
};

const BankQuestion = ({ teacherBankCats }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [successMsg, setSuccessMsg] = useState(false);
  const [fileProgress, setFileProgress] = useState(null);
  const [questionData, setQuestionData] = useState(initialQuestionData);

  const { userProfileData } = useAppSelector((state) => state.store);
  const dispatch = useAppDispatch();

  const errorFn = (text?: string) => {
    dispatch(
      pushModal(<GeneralMessageModal msg={text ?? "حدث خطأ"} btnMsg="حسنا" />)
    );
  };

  return (
    <form
      className="p-3 space-y-6"
      onSubmit={async (e) => {
        e.preventDefault();

        if (!questionData.imgSrc && !questionData.qText)
          return errorFn("يرجى تحديد صورة أو محتوي للسؤال");
        if (!questionData.qCat) return errorFn("يرجى تحديد المجموعة");
        if (!questionData.qWeight) return errorFn("يرجى تحديد الدرجة");
        if (!questionData.qAns) return errorFn("يرجى تحديد الإجابة");
        if (!questionData.qAnsCount) return errorFn("يرجى تحديد عدد الإجابات");

        await handlePublishQuestion(
          setIsLoading,
          questionData,
          setSuccessMsg,
          errorFn
        );
        
        setQuestionData({
          title: "",
          imgSrc: null,
          qText: "",
          qAnsCount: 2,
          qAns: "",
          qWeight: "",
          qCat: "",
        });
      }}
    >
      <div className="flex flex-col gap-2">
        <label htmlFor="quiz_question_img_input">صورة السؤال (اختياري)</label>
        {fileProgress && fileProgress < 100 && fileProgress >= 0 && (
          <FileUploadProgressBar fileProgress={fileProgress} />
        )}
        {!questionData?.imgSrc && (
          <Button
            type="button"
            variant="outline"
            className="hover:bg-purple-200 w-full h-fit py-5 rounded-lg bg-purple-100 border-2 border-dashed border-purple-500 flex flex-col gap-2 items-center justify-center"
            onClick={() =>
              document.getElementById("quiz_question_img_input")?.click()
            }
          >
            <Upload className="size-10" />
            <p className="no-underline">إفع صورة السؤال</p>
          </Button>
        )}

        <input
          id="quiz_question_img_input"
          onChange={(e) => {
            if (!e.target.files[0]) {
              dispatch(
                pushModal(
                  <GeneralMessageModal msg="يرجى اختيار ملف." btnMsg="حسنا" />
                )
              );
              return;
            }

            handleUploadFb(
              e,
              setFileProgress,
              setQuestionData,
              questionData,
              errorFn
            );
          }}
          hidden
          className="hidden elite-file lesson_form_input form-control mt-2"
          type="file"
        />
        <input value={questionData.imgSrc} className="hidden" hidden required />
      </div>
      {questionData.imgSrc && (
        <QuestionImg
          id={`uploaded-image`}
          src={questionData.imgSrc}
          alt=""
          className="m-auto mb-2 img-fluid rounded-lg"
          border="3px solid #673de6"
          onImgDelete={() =>
            setQuestionData((prev) => ({ ...prev, imgSrc: "" }))
          }
        />
      )}
      {/* <div
        className="spinner-border spinner"
        role="status"
        style={{ display: "none" }}
      >
        <span className="visually-hidden">Loading...</span>
      </div> */}
      <div className="flex flex-col gap-2">
        <label htmlFor="exampleFormControlTextarea1">
          محتوى السؤال (اختياري)
        </label>
        <textarea
          value={questionData.qText}
          onChange={(e) =>
            setQuestionData((prev) => ({ ...prev, qText: e.target.value }))
          }
          className="w-full border border-gray-400 p-3 rounded-lg"
          id="exampleFormControlTextarea1"
          rows={3}
        ></textarea>
      </div>
      <div className="flex flex-col gap-2">
        <label htmlFor="quiz_question_ans_count">عدد الإجابات</label>
        <input
          id="quiz_question_ans_count"
          type="number"
          className="rounded-lg border border-gray-400 p-2 focus:ring-(--primary-color-light) w-full md:w-[30%] md:min-w-fit"
          required
          value={questionData.qAnsCount}
          onChange={(e) => maxMinCount(e.target.value, setQuestionData)}
        />
      </div>
      <select
        className="rounded-lg border border-gray-400 p-2 focus:ring-(--primary-color-light) w-full md:w-[30%] md:min-w-fit"
        required
        value={questionData.qAns}
        onChange={(e) =>
          setQuestionData((prev) => ({ ...prev, qAns: e.target.value }))
        }
      >
        <option value="">الإجابة الصحيحة</option>
        {getAnswersCount(questionData).map((ans) => (
          <option value={ans}>{ans}</option>
        ))}
      </select>
      <div className="flex flex-col gap-2">
        <label htmlFor="quiz_question_weight">الدرجة</label>
        <input
          value={questionData.qWeight}
          onChange={(e) =>
            setQuestionData((prev) => ({ ...prev, qWeight: e.target.value }))
          }
          id="quiz_question_weight"
          type="number"
          className="rounded-lg border border-gray-400 p-2 focus:ring-(--primary-color-light) w-full md:w-[30%] md:min-w-fit"
          required
        />
      </div>
      <div className="flex flex-col gap-6">
        <select
          className="rounded-lg border border-gray-400 p-2 focus:ring-(--primary-color-light) w-full md:w-[30%] md:min-w-fit"
          required
          value={questionData.qCat}
          onChange={(e) =>
            setQuestionData((prev) => ({ ...prev, qCat: e.target.value }))
          }
        >
          <option value="">إختر المجموعة الخاص بالسؤال</option>
          {teacherBankCats.map((ans) => (
            <option value={ans}>
              {ans.indexOf(` - ${userProfileData.teacher_prefix}`) > -1
                ? ans.replace(` - ${userProfileData.teacher_prefix}`, "")
                : ans}
            </option>
          ))}
        </select>

        <Button
          disabled={
            fileProgress && fileProgress < 100 && fileProgress >= 0
              ? true
              : false
          }
          id="publish_new_quiz_submit"
          className="w-fit min-w-[30%]"
        >
          {isLoading ? <Spinner variant="button" /> : "إضافة للبنك"}
        </Button>
      </div>
      {successMsg && (
        <div className="w-fit py-1 px-4 border bg-green-100 text-green-700 border-green-400 rounded-lg">
          تم إضافة السؤال لبنك الأسئلة بنجاح
        </div>
      )}
    </form>
  );
};

export default BankQuestion;
